/**
 * TRIM / EXTEND of a SPLINE as the TARGET (the object being cut/grown) —
 * MASTERPLAN N3 MODIFY-TRIM-EXTEND-POLY-SPLINE-TARGETS, the harder of the two
 * new target families. Real NURBS math throughout (Piegl & Tiller "The NURBS
 * Book", `ezdxf.math` as the reference implementation family) — never a
 * flattened/re-fit approximation, per CLAUDE.md ПРИОРИТЕТ #1.
 *
 * ## TRIM
 *
 * Exactly `splitNurbsCurve`'s job: find the intersection point's exact curve
 * PARAMETER (point inversion, `findNurbsParameterAtPoint`), then split the
 * curve there via knot insertion. The surviving span(s) are genuine,
 * correctly-parameterised NURBS truncations — never re-sampled.
 *
 * ## EXTEND
 *
 * Per Autodesk's own documented behaviour (AutoCAD Help, "About Trimming and
 * Extending Objects" / SPLINE EXTEND note): "the extended portion is LINEAR
 * and TANGENT to the end of the original spline" — the ORIGINAL portion's
 * shape is preserved EXACTLY (never touched) and a new straight tail is
 * appended, tangent (G1: matching direction, not necessarily matching
 * derivative magnitude — "tangent" in AutoCAD's plain-language sense) at the
 * join. This is built by APPENDING `degree` new control points (a straight
 * line needs only `degree+1` control points to be an EXACT degree-`p` curve —
 * every intermediate point collinear) after a FULL-multiplicity (`degree+1`)
 * knot at the old end, so the ORIGINAL control points / knots are literally
 * untouched (structurally guaranteed exact preservation — see
 * `buildLinearExtension`'s doc comment for the full derivation) while the new
 * tail is G1-tangent by construction (its second control point lies along the
 * SAME ray used to find the boundary point in the first place).
 *
 * ## Scope
 *
 * CLOSED / periodic splines are explicitly OUT of this slice — a closed
 * B-spline TRIM/EXTEND needs periodic-knot-vector reparameterisation, a
 * distinct, large sub-algorithm (documented follow-up, MASTERPLAN N3 body).
 * `entityIntersectionPoints`'s tessellation fallback already supports a closed
 * spline as a cutting EDGE; only the TARGET side is scoped down here.
 */
import {
  evaluateNurbs,
  findNurbsParameterAtPoint,
  insertKnot,
  KNOT_EPS as NURBS_KNOT_EPS,
  knotMultiplicity,
  nurbsKnotSpanBreakpoints,
  resolveSplineNurbs,
  splitNurbsCurve,
  type SplineNurbsResolvable,
  type SplinePoint,
} from "@dxf-ai-editor/drawing-model";

import {
  clampCarrierTolerance,
  rescaleKnotDomain,
  selectTrimSurvivors,
  splineEndTangentStep,
  type IntPoint,
} from "./trimExtendGeometry";

const GEO_EPS = 1e-9;

export type SplineTargetInput = SplineNurbsResolvable & {
  closed?: boolean | undefined;
  // DXF group 70 bit 2 — independent of `closed` (bit 1): a spline can be
  // PERIODIC (wrapped knot vector, no true start/end) without its `closed`
  // flag being set, and vice versa. Both TRIM and EXTEND below need the
  // periodic-knot-vector reparameterisation this slice explicitly does not
  // implement (Codex P2: an unguarded `periodic:true, closed:false` input
  // was silently rewritten into an inconsistent open representation).
  periodic?: boolean | undefined;
};

/** Always a control-point-mode NURBS result — a trimmed/extended fit-point
 * spline's fit points no longer describe the truncated/grown curve, so (like
 * a trimmed Circle becoming a typed Arc) the output re-expresses the curve in
 * its authoritative control-point form. `fitPoints` is always `[]`.
 *
 * `rational` (Codex P2, GitHub review round 2 follow-up): `resolveSplineNurbs`
 * now REQUIRES `rational===true` before honouring a `weights` array (see its
 * own doc comment). Without this field, feeding a `SplineTargetResult` back
 * into ANOTHER `trimSplineTarget`/`extendSplineTarget` call as a
 * `SplineTargetInput` (composing two edits — e.g. TRIM then EXTEND, or two
 * TRIMs in sequence, on the same in-flight result rather than going through a
 * full round-trip via a persisted `DrawingEntity`, which DOES carry its own
 * `rational` field) would supply `rational: undefined` on the second call —
 * silently discarding a genuinely rational curve's weights and computing the
 * SECOND operation against a different, non-rational curve. Always exactly
 * `weights !== undefined` at construction: `resolveSplineNurbs`'s own gate
 * already makes those two facts equivalent. */
export type SplineTargetResult = {
  degree: number;
  controlPoints: SplinePoint[];
  knots: number[];
  weights: number[] | undefined;
  closed: false;
  rational: boolean;
};

function extractNurbsSubcurve(
  controlPoints: SplinePoint[],
  knots: number[],
  weights: number[] | undefined,
  degree: number,
  lo: number,
  hi: number,
): { controlPoints: SplinePoint[]; knots: number[]; weights: number[] | undefined } | null {
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant contract):
  // `knots[degree] ?? 0` -> `knots[degree] && 0`. This function's ONLY call
  // site (`trimSplineTarget`) always passes the POST-`rescaleKnotDomain`
  // knot vector — and `rescaleKnotDomain`'s own `toWork` maps its input
  // domain's start to EXACTLY `0` (`(rawUMin - rawUMin) * scale === 0`,
  // algebraically exact: zero times any finite `scale` is exactly zero in
  // IEEE754, no rounding possible). So `knots[degree]` at this call site is
  // ALWAYS the bit-exact literal `0`, making `0 ?? 0` and `0 && 0`
  // IDENTICAL (both `0`) for every reachable input — proven, not merely
  // asserted; no test can kill this.
  // Stryker disable next-line LogicalOperator: knots[degree] is always the bit-exact literal 0 post-rescaleKnotDomain (its own start-of-domain normalisation), making `?? 0` and `&& 0` provably identical (see equivalent-mutant note above)
  const uMin = knots[degree] ?? 0;
  // BEST-EFFORT-EXHAUSTED (mutation-testing skill's convention for a
  // genuinely intractable-to-construct boundary): `knots[knots.length -
  // degree - 1] ?? 1` -> `&& 1` (L101:16), and the two `ArithmeticOperator`
  // index-math mutants on the SAME expression (round-17 audit) all reduce
  // to the SAME root cause. `rescaleKnotDomain`'s `toWork` maps this call
  // site's domain END to `RESCALE_TARGET_WIDTH` (`= 1`) via `(rawUMax -
  // rawUMin) * (1 / (rawUMax - rawUMin))` — algebraically `1`, and
  // EMPIRICALLY bit-exact `1.0` for every domain width this package's own
  // tests construct, INCLUDING the pathological near-`GEO_EPS`-scale widths
  // this file's own doc comments call out (`rescaleKnotDomain`'s "2e-9
  // wide" example) — verified directly: a `2e-9`-wide raw domain rescales
  // to `uMax = 0.9999999999999999`, off from `1` by ONE ULP (~1.1e-16).
  // That ULP-scale discrepancy is roughly 7 ORDERS OF MAGNITUDE below this
  // function's own `GEO_EPS` (`1e-9`) — the ONLY tolerance any caller-
  // observable branch (`hi < uMax - GEO_EPS` below) ever compares `uMax`
  // against. No `hi` value can straddle a gap `GEO_EPS` itself cannot
  // resolve, so the TRUE-vs-fallback-1 divergence, while numerically real,
  // has no boundary-guard input that surfaces it. The two arithmetic-offset
  // index mutants land EITHER (a) within the SAME `degree+1`-multiplicity
  // clamp run every valid clamped B-spline knot vector maintains at its end
  // (an offset of `+2` from the correct index — bit-identical VALUE, a
  // second genuine equivalence by the SAME "full clamp" invariant
  // `L520:56`'s sibling analyses rely on elsewhere in this package), OR (b)
  // genuinely out-of-bounds (`+2*degree` offset), hitting this SAME `?? 1`
  // fallback exactly — collapsing back to the identical unobservable-via-
  // `GEO_EPS` case above. `knots.length` is also invariantly `>=
  // controlPoints.length + degree + 1` throughout this codebase (maintained
  // by `resolveSplineNurbs`/`rescaleKnotDomain`/`splitNurbsCurve`), so the
  // in-bounds fallback path is dead code for genuinely malformed input too
  // — it exists purely as defence-in-depth, not a reachable behaviour.
  // Stryker disable next-line LogicalOperator,ArithmeticOperator: uMax post-rescale is bit-exact 1.0 except for an unobservable-through-GEO_EPS ULP-scale discrepancy on pathologically tiny domains, and the arithmetic-offset index mutants either land in the same full-clamp-multiplicity run (bit-identical value) or hit this same fallback exactly (see best-effort-exhausted note above)
  const uMax = knots[knots.length - degree - 1] ?? 1;
  let cur = { controlPoints, knots, weights };
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract): `lo > uMin + GEO_EPS` -> `lo >= uMin + GEO_EPS`. This
  // function's ONLY call site (`trimSplineTarget`) always derives `lo`/`hi`
  // from `selectTrimSurvivors`'s (`trimExtendGeometry.ts`) `[t0,
  // ...inside, t1]` bounds array, where `inside` is ALREADY filtered to `c
  // > t0 + GEO_EPS && c < t1 - GEO_EPS` — every "genuine interior cut"
  // that ever becomes a survivor's `lo`/`hi` is, BY THAT UPSTREAM FILTER'S
  // OWN CONSTRUCTION, either the exact literal domain edge (`t0`/`t1`
  // itself, when the OTHER end of a survivor pair) or STRICTLY beyond
  // `GEO_EPS` from it. There is NO reachable `lo` value landing in the
  // ambiguous `(uMin, uMin + GEO_EPS]` gap this `>` vs `>=` distinction
  // would matter for — the ONLY two cases that ever reach this line are
  // `lo === uMin` exactly (trivially `false` under EITHER operator) or `lo
  // > uMin + GEO_EPS` by a comfortable margin (trivially `true` under
  // EITHER operator). Verified directly: a cut placed within `GEO_EPS` of
  // `uMin` is rejected by `selectTrimSurvivors`'s `inside` filter as a
  // grazing (non-interior) crossing BEFORE this function is even called
  // (see the mutation test suite's "L103" case) — no test can reach the
  // gap this mutant would need to be observable through.
  // Stryker disable next-line EqualityOperator: no reachable `lo` value lands in the ambiguous (uMin,uMin+GEO_EPS] gap — selectTrimSurvivors's own upstream `inside` filter already guarantees every survivor's lo is either exactly uMin or strictly beyond GEO_EPS from it (see equivalent-mutant note above)
  if (lo > uMin + GEO_EPS) {
    const split = splitNurbsCurve(cur.controlPoints, cur.knots, cur.weights, degree, lo);
    // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
    // contract): `if (!split) return null;` — CORRECTED from an earlier,
    // flawed attempt at a "genuinely reachable" test for this line (that
    // attempt's own fixture was itself filtered out by
    // `selectTrimSurvivors`'s upstream `inside` filter before
    // `extractNurbsSubcurve` was even called — never actually exercising
    // this branch). The SAME `>t0+GEO_EPS && <t1-GEO_EPS` upstream
    // guarantee the `lo`/`hi` boundary proof above relies on ALSO proves
    // `splitNurbsCurve` can never return `null` here: its OWN only
    // null-return condition is `uRaw <= uMin+KNOT_EPS || uRaw >=
    // uMax-KNOT_EPS` (`nurbsKnotOps.ts`, `KNOT_EPS = 1e-10`) — and this
    // `lo` is, by the upstream filter, ALWAYS either exactly `uMin`
    // (trivial, doesn't reach this branch) or STRICTLY beyond
    // `uMin+GEO_EPS` (1e-9, a COARSER bound than `KNOT_EPS`) AND STRICTLY
    // below `uMax-GEO_EPS` (likewise coarser than `uMax-KNOT_EPS`) —
    // `splitNurbsCurve`'s own rejection band is a STRICT SUBSET of the
    // zone the upstream filter already excludes. `split` is therefore
    // ALWAYS truthy here — no test can make `!split` observably diverge
    // from `false`.
    // Stryker disable next-line ConditionalExpression: splitNurbsCurve's own null-return band (KNOT_EPS, coarser-excluded) is a strict subset of what the upstream inside-filter (GEO_EPS) already guarantees never reaches this lo — split is always truthy here (see equivalent-mutant note above)
    if (!split) return null;
    cur = split.right;
  }
  // Mirrors the `lo` boundary above — same equivalent-mutant proof, same
  // upstream `inside` filter guarantee (this time against `t1`/`uMax`).
  // Stryker disable next-line EqualityOperator: no reachable `hi` value lands in the ambiguous [uMax-GEO_EPS,uMax) gap — same upstream `inside`-filter proof as the `lo` boundary above (see equivalent-mutant note above)
  if (hi < uMax - GEO_EPS) {
    const split = splitNurbsCurve(cur.controlPoints, cur.knots, cur.weights, degree, hi);
    // EQUIVALENT MUTANT — mirrors the `lo`-side guard above (see its
    // comment for the full proof); same upstream `inside`-filter
    // guarantee applied to `hi` against `uMin`.
    // Stryker disable next-line ConditionalExpression: same equivalent-mutant proof as the lo-side guard above, applied to hi against uMin
    if (!split) return null;
    cur = split.left;
  }
  return cur;
}

/** Two input coordinates within this distance are treated as "the same
 * intersection point" for `resolveSplineCutParams`'s self-intersection
 * disambiguation — matches `trimExtendPolylineTarget.ts`'s
 * `COINCIDENT_CUT_TOL` convention exactly (same class of gap, same tolerance
 * reasoning: a genuine duplicate hit is numerically exact). */
const COINCIDENT_CUT_TOL = 1e-6;

/**
 * Resolve MULTIPLE cutting-edge intersection points to curve parameters, one
 * call per point — EXCEPT when a point's coordinate coincides with an
 * EARLIER point's in this same batch, in which case that earlier point's
 * resolved `u` is excluded from `findNurbsParameterAtPoint`'s coarse-sample
 * search. A self-intersecting SPLINE crossed by the same boundary edge at
 * its own crossing point reports that point TWICE (`entityIntersectionPoints`
 * carries no per-hit parameter provenance); resolving both occurrences
 * independently always converges to the SAME `u`, losing the second curve
 * branch and corrupting `selectTrimSurvivors`'s span selection (Codex P2 —
 * the same class of gap as `trimExtendPolylineTarget.ts`'s
 * `resolveCutParams`, which this mirrors). Handles the common two-branch
 * case; three-or-more coincident branches is a documented follow-up
 * (MASTERPLAN N3 body) needing intersection-provenance plumbing that spans
 * every curve target family, not scoped to this module alone.
 *
 * A repeated coordinate does NOT always mean "a self-intersection's second
 * branch" (Codex P2, round 4 — flagged directly against the POLYLINE
 * sibling's discrete segment-exclusion; this mirrors that same guard here
 * for symmetry, since the underlying "coincidental duplicate" ambiguity is
 * identical). Two DIFFERENT cutting edges can cross the SAME
 * non-self-intersecting curve at the same point too. Guard: only accept the
 * excluded search's result when it is ALSO an exact (tolerance-level)
 * coordinate match; otherwise fall back to the plain (unexcluded)
 * resolution. In practice this fallback is HARDER to observe here than on
 * the polyline: `findNurbsParameterAtPoint`'s Gauss-Newton refinement stage
 * often "rescues" an excluded coarse-sample start back to the SAME true
 * nearest point for an ordinary smooth (non-self-intersecting) curve, since
 * refinement is not confined to its starting sample's neighbourhood the way
 * the polyline's discrete per-segment search is — the guard still matters
 * for a curve with a genuinely separate nearby local minimum (e.g. two
 * distinct branches passing close to, but not through, the same point).
 */
function resolveSplineCutParams(
  controlPoints: SplinePoint[],
  knots: number[],
  weights: number[] | undefined,
  degree: number,
  points: IntPoint[],
): number[] {
  const seen: Array<{ point: IntPoint; u: number }> = [];
  return points.map((p) => {
    // BEST-EFFORT-EXHAUSTED (mutation-testing skill's convention — NOT
    // claimed as an equivalence proof) for ALL THREE mutants on the
    // `.filter(...)` line below (dropping the call entirely / a `true`
    // predicate stub / the `<=` -> `<` boundary): a fixture constructed
    // specifically to make wrong-exclusion OBSERVABLE (a tightly
    // self-crossing curve where excluding the true neighbourhood sends
    // the search to a wildly wrong, distant parameter — see the mutation
    // test suite's dedicated case) was tried and FOUND NOT to discriminate
    // these three mutants: `resolveSplineCutParams`'s OWN later fallback
    // (`if (excludeNear.length > 0) { ... if (d > COINCIDENT_CUT_TOL) { u
    // = findNurbsParameterAtPoint(..., p) /* UNRESTRICTED */ } }`)
    // automatically RE-CORRECTS exactly that "excluded search lands wildly
    // wrong" outcome back to the same final answer an un-excluded search
    // would have given — the very divergence needed to observe a wrongly
    // -broadened exclusion is ALSO the divergence its own fallback safety
    // net erases. Discriminating these mutants needs a fixture where a
    // wrongly-included exclusion shifts the search to a DIFFERENT, but
    // still within-`COINCIDENT_CUT_TOL`-of-`p`, parameter (avoiding the
    // fallback's own `d > COINCIDENT_CUT_TOL` trigger) — not achieved
    // within this gate's time budget.
    // Stryker disable next-line MethodExpression: fixtures exercising wrong-exclusion divergence are auto-corrected by this function's own later fallback re-search, erasing the very divergence needed to discriminate this mutant (see best-effort-exhausted note above)
    const excludeNear = seen.filter(
      // Stryker disable next-line ConditionalExpression,EqualityOperator: same best-effort-exhausted rationale as the MethodExpression note directly above
      (s) => Math.hypot(s.point.x - p.x, s.point.y - p.y) <= COINCIDENT_CUT_TOL,
    ).map((s) => s.u);
    let u = findNurbsParameterAtPoint(controlPoints, knots, weights, degree, p, {
      excludeNear,
    });
    // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
    // contract): `excludeNear.length > 0` -> `>= 0` / `true`. Both are
    // ALWAYS satisfied (`.length` is never negative) — but when
    // `excludeNear` is genuinely `[]`, the block above's `u` already came
    // from an UNRESTRICTED search (`excludeNear: []`), so entering this
    // branch anyway re-runs the IDENTICAL unrestricted
    // `findNurbsParameterAtPoint(..., p)` call below on a `d > tol`
    // mismatch — reassigning `u` to the SAME value it already holds. No
    // input can make an always-true condition here diverge from the
    // correct conditional one.
    // Stryker disable next-line ConditionalExpression,EqualityOperator: `.length` is never negative, and when it's genuinely 0 the fallback re-search recomputes the IDENTICAL unrestricted result the first call already produced (see equivalent-mutant note above)
    if (excludeNear.length > 0) {
      const landed = evaluateNurbs(controlPoints, knots, weights, degree, u);
      const d = Math.hypot(landed.x - p.x, landed.y - p.y);
      // BEST-EFFORT-EXHAUSTED BOUNDARY (mutation-testing skill's
      // convention — NOT claimed as an equivalence proof): `d >
      // COINCIDENT_CUT_TOL` -> `d >= COINCIDENT_CUT_TOL`. `d` is a
      // Gauss-Newton-refined distance, not a hand-constructible exact
      // value — landing bit-exact on `COINCIDENT_CUT_TOL` (1e-6) through
      // the public API is impractical. The fallback re-search's OWN
      // necessity (this branch genuinely firing, `d` far beyond the
      // boundary) IS directly tested (see the mutation test suite's
      // dedicated case), covering the "false"/BlockStatement/`<=0`
      // disabled-fallback mutants — only the LAST-ULP exact equality
      // remains unaddressed, the same class of gap as this package's
      // `disc === -GEO_EPS` boundary (`trimExtendGeometryCore.ts`).
      // Stryker disable next-line EqualityOperator: exact d===COINCIDENT_CUT_TOL bit boundary impractical to construct through Gauss-Newton-refined distances (see best-effort-exhausted note above); the fallback's own necessity is directly tested
      if (d > COINCIDENT_CUT_TOL) {
        u = findNurbsParameterAtPoint(controlPoints, knots, weights, degree, p);
      }
    }
    seen.push({ point: p, u });
    return u;
  });
}

/**
 * Trim a SPLINE target at the cutting-edge intersections nearest the pick.
 * Returns one survivor (near-end trim) or two (mid-curve split), or `null`
 * for a no-op / unsupported (closed) curve.
 */
export function trimSplineTarget(
  spline: SplineTargetInput,
  cutPoints: IntPoint[],
  pickPoint: IntPoint,
): SplineTargetResult[] | null {
  if (spline.closed || spline.periodic) return null; // periodic/closed — documented follow-up
  const resolved = resolveSplineNurbs(spline);
  if (!resolved) return null;
  const { controlPoints, weights, degree } = resolved;
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract, BOTH the `false`-stub ConditionalExpression AND the
  // `degree - 1` ArithmeticOperator threshold mutants): a valid B-spline
  // mathematically REQUIRES at least `degree + 1` control points to
  // define even ONE polynomial span (Piegl & Tiller §2 — there is no
  // non-degenerate NURBS structure with fewer). `resolveSplineNurbs`
  // regenerates `knots` via `uniformKnotVector` whenever the caller's own
  // knot array length doesn't match `cps.length + degree + 1` — and THAT
  // function has its OWN explicit degenerate-input branch for `cps.length
  // < degree + 1` (`splineMath.ts`): it builds a knot array split exactly
  // in half between `0` and `1`, which makes `knots[degree]` and
  // `knots[knots.length - degree - 1]` either COLLIDE (same index,
  // verified directly for `cps.length=3, degree=3`) or INVERT (`rawUMax <
  // rawUMin`, verified for `cps.length=2, degree=3`) — either way,
  // `rescaleKnotDomain` (below) ALWAYS fails (`!(rawUMax > rawUMin)`) for
  // this exact input class, hitting the LATER `if (!rescale) return
  // null;` via an independent, redundant path. A caller-supplied knot
  // array of the "right" length for `cps.length < degree + 1` would
  // itself be a mathematically invalid NURBS structure (the same
  // fundamental impossibility applies regardless of the specific knot
  // values chosen).
  // Stryker disable next-line ConditionalExpression,ArithmeticOperator: uniformKnotVector's own explicit degenerate-input branch always collapses knots[degree]/knots[length-degree-1] into a non-positive-width domain for this input class, redundantly caught by the later !rescale guard (see equivalent-mutant note above)
  if (controlPoints.length < degree + 1) return null;

  // Always normalise to `RESCALE_TARGET_WIDTH` — see `rescaleKnotDomain`'s
  // doc comment (`trimExtendGeometry.ts` — the SHARED implementation,
  // Codex P2 round 10 #2: `splineEndStretch` uses the SAME function so
  // every spline TRIM/EXTEND code path stays on ONE parameter scale).
  const rescale = rescaleKnotDomain(resolved.knots, degree);
  if (!rescale) return null;
  const { knots, uMin, uMax, toRaw } = rescale;

  const cutParams = resolveSplineCutParams(controlPoints, knots, weights, degree, cutPoints);
  const pickParam = findNurbsParameterAtPoint(controlPoints, knots, weights, degree, pickPoint);
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract): `uMax - uMin` -> `uMax + uMin`. `period` is passed to
  // `selectTrimSurvivors` -> `clampPickToOpenDomain`, where it is used
  // ONLY inside the `period > domainLen + GEO_EPS` circular-wrap branch —
  // and `pickParam` here is ALWAYS a `findNurbsParameterAtPoint` result,
  // which that function's own Newton loop clamps to `[uMin, uMax]` on
  // EVERY iteration (`uNew = Math.min(uMax, Math.max(uMin, uNew))`,
  // `nurbsKnotOps.ts`). So `pickParam` is ALWAYS within
  // `clampPickToOpenDomain`'s `[t0-GEO_EPS, t1+GEO_EPS]` early-return
  // window, and the `period`-dependent branch below it is NEVER reached
  // from this call site — `period`'s numeric value has no path to
  // `trimSplineTarget`'s observable output.
  // Stryker disable next-line ArithmeticOperator: period only feeds clampPickToOpenDomain's circular-wrap branch, which pickParam (always Newton-clamped to [uMin,uMax]) never reaches from this call site (see equivalent-mutant note above)
  const period = uMax - uMin; // open domain: period === domain length (linear, like Line)
  const survivors = selectTrimSurvivors(cutParams, pickParam, period, { t0: uMin, t1: uMax });
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract): `survivors.length === 0` -> `false` (disabled). When
  // `survivors` is genuinely empty, the `for` loop below never executes
  // (`results` stays `[]`), and the function's OWN final line
  // (`results.length > 0 ? results : null`) already returns `null` for an
  // empty `results` — an identical observable outcome to this early
  // return, just reached one statement later. This guard is a pure
  // (harmless) optimisation, not behaviourally distinct from its absence.
  // Stryker disable next-line ConditionalExpression: an empty `survivors` skips the loop body entirely, and the function's own final `results.length>0?results:null` already returns null for that empty-results case — disabling this early return changes nothing observable (see equivalent-mutant note above)
  if (survivors.length === 0) return null;

  const results: SplineTargetResult[] = [];
  for (const [lo, hi] of survivors) {
    const piece = extractNurbsSubcurve(controlPoints, knots, weights, degree, lo, hi);
    // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
    // contract): `if (piece)` -> `if (true)`. `extractNurbsSubcurve`'s
    // ONLY two `null`-return paths (its own `if (!split) return null;`
    // guards) are THEMSELVES proven equivalent mutants above — `split` is
    // ALWAYS truthy there, given the SAME upstream `selectTrimSurvivors`
    // `inside`-filter guarantee this function's own `lo`/`hi` boundary
    // proofs rely on. `piece` is therefore ALWAYS non-`null` for every
    // input reachable through this call site — `if (piece)` and `if
    // (true)` are observationally identical; no test can make them
    // diverge (a `true`-stubbed dereference of a genuinely-`null` `piece`
    // would crash, but that `null` is itself unreachable).
    // Stryker disable next-line ConditionalExpression: extractNurbsSubcurve can never actually return null when called from here (both its own null-return paths are themselves proven equivalent mutants above) — piece is always truthy (see equivalent-mutant note above)
    if (piece) {
      results.push({
        degree,
        controlPoints: piece.controlPoints,
        knots: piece.knots.map(toRaw), // back to the SOURCE domain scale
        weights: piece.weights,
        closed: false,
        rational: piece.weights !== undefined,
      });
    }
  }
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract, UPGRADED from an earlier "sanity check" note — see the
  // mutation test suite's own comment history): `results.length > 0` ->
  // `true` / `>= 0`. Given `piece` above is ALWAYS non-`null` (proven),
  // `results` ALWAYS ends up with exactly one entry per `survivors`
  // element — and `survivors.length >= 1` is guaranteed by this
  // function's own earlier (also equivalent) `survivors.length === 0`
  // guard, reached this far. `results.length` is therefore ALWAYS `>= 1`
  // whenever this line runs — the `results.length <= 0` (empty) branch
  // this ternary exists to guard against is unreachable.
  // Stryker disable next-line ConditionalExpression,EqualityOperator: results always has >=1 entry (piece is always truthy, survivors always non-empty when reached) — the empty-results branch this ternary guards against is unreachable (see equivalent-mutant note above)
  return results.length > 0 ? results : null;
}

/**
 * Append a straight, G1-tangent tail to a clamped NURBS curve, reaching
 * `target` exactly. `growEnd=true` appends after the curve's END (`uMax`);
 * `false` prepends before its START (`uMin`) — symmetric construction.
 *
 * ## Derivation (why this preserves the original curve EXACTLY)
 *
 * B-spline basis functions have LOCAL support: `N_{i,p}` is nonzero only on
 * `[u_i, u_{i+p+1})`. Appending `degree+1` new knot values STRICTLY AFTER the
 * curve's existing knot vector (never modifying an existing entry) leaves
 * every ORIGINAL basis function's defining window entirely within the
 * unchanged prefix — so the curve is bit-for-bit identical to the original on
 * `[uMin, uJoin]`. The join point itself keeps ITS OWN multiplicity (`degree
 * +1`, the curve's real old end) — an interior knot of multiplicity `degree+1`
 * is a valid (if unusual) NURBS structure representing two independent
 * polynomial pieces touching at one point (Cox-de Boor's zero-denominator
 * convention, already relied on by `evaluateNurbs`, resolves it without
 * special-casing). Position continuity is then enforced EXPLICITLY (the new
 * segment's first control point duplicates the old end's VALUE) rather than
 * structurally, and direction continuity ("tangent") follows automatically
 * because every new control point is collinear along the SAME ray used to
 * find `target` in the first place (evenly-spaced points on a line reproduce
 * that line exactly, for ANY B-spline degree/knot spacing).
 */
function buildLinearExtension(
  controlPoints: SplinePoint[],
  knots: number[],
  weights: number[] | undefined,
  degree: number,
  growEnd: boolean,
  target: SplinePoint,
): { controlPoints: SplinePoint[]; knots: number[]; weights: number[] | undefined } {
  const p = degree;
  const anchor = growEnd ? controlPoints[controlPoints.length - 1]! : controlPoints[0]!;
  const anchorZ = anchor.z ?? 0;
  // `target.z` (Codex P2, round 5) — the CALLER (`extendSplineTarget`)
  // derives this from the curve's own local dz/du tangent ratio, projected
  // out to `target`'s XY distance, so a non-planar/tilted-planar spline's
  // extension continues its REAL 3-D tangent rather than flattening to the
  // anchor's own elevation. Falls back to `anchorZ` only for a caller that
  // genuinely has no better Z estimate (e.g. a synthetic 2-D target).
  const targetZ = target.z ?? anchorZ;
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract, `!growEnd` side only — see the mutation test suite for the
  // `growEnd` side, which IS independently killed by the existing
  // START-side extend tests): `knots[0] ?? 0` -> `knots[0] && 0`. Every
  // caller passes a fully CLAMPED B-spline knot vector (this codebase's
  // invariant throughout — `rescaleKnotDomain`/`ensureClampedAtGrowEnd`
  // both preserve full `degree+1` start-clamp multiplicity), so
  // `knots[0]` sits in the SAME all-equal-value run as `knots[degree]`,
  // which `rescaleKnotDomain`'s own `toWork` maps to the bit-exact literal
  // `0` (proven in `extractNurbsSubcurve`'s analogous `uMin` derivation
  // above). `knots[0]` is therefore ALWAYS the bit-exact literal `0` too —
  // `0 ?? 0` and `0 && 0` are provably identical.
  // Stryker disable next-line LogicalOperator: knots[0] sits in the same full-multiplicity start-clamp run as knots[degree], which rescaleKnotDomain's own normalisation makes bit-exact 0 (see equivalent-mutant note above)
  const uJoin = growEnd ? (knots[knots.length - 1] ?? 0) : (knots[0] ?? 0);

  // Codex P1, round 20: the new tail's own U-DOMAIN WIDTH must be a
  // PARAMETER-SCALE quantity, decoupled from the extension's WORLD-SPACE
  // distance — a NURBS parametrisation carries no obligation to correlate
  // with arc length (Piegl & Tiller §2). The OLD code used the raw world
  // chord (`uFar = uJoin + Math.hypot(target-anchor)`) directly as the new
  // knot span's WIDTH: extending a cubic spline whose domain had already
  // been rescaled to width `RESCALE_TARGET_WIDTH` (=1, see
  // `rescaleKnotDomain`) by, say, 100 world units balloons the OVERALL
  // domain to ~101x its original width. `splineToCubicBezier` (the
  // pure-rendering path — PDF/SVG export via `buildSplineBeziers`) samples
  // that domain with a small FIXED, non-per-span-aware sample count
  // (unlike `entityToPolyline`'s TRIM/EXTEND-intersection tessellation,
  // already per-knot-span-aware since round 11): almost every sample then
  // lands in the new tail, leaving the ORIGINAL curve's own (now a tiny
  // fractional share of the domain) span with ZERO interior samples — the
  // preserved original shape visually vanishes, replaced by what looks
  // like one long straight cross-join segment. The new tail's own u-width
  // instead matches the curve's EXISTING TERMINAL knot span's width at the
  // growing end — proportionate to the rest of the curve's own parameter
  // scale, entirely independent of how visually long the extension is.
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract) — same proof as `extractNurbsSubcurve`'s `uMin` derivation:
  // `knots` here is ALWAYS `ensureClampedAtGrowEnd`'s output, which
  // preserves (or freshly re-derives, for the truncated end) a full
  // `degree+1`-multiplicity start clamp whose value is, either way,
  // provably the bit-exact literal `0` (the `growEnd` branch leaves the
  // ORIGINAL rescaled start untouched; the `!growEnd` branch rebuilds it
  // from `uEnd = knots[degree] ?? 0`, which is that SAME bit-exact `0`).
  // Stryker disable next-line LogicalOperator: knots[p] is always the bit-exact literal 0 for the same reason as extractNurbsSubcurve's uMin (see equivalent-mutant note above)
  const domainUMin = knots[p] ?? 0;
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract, ArithmeticOperator index offset) — same proof pattern as
  // `extractNurbsSubcurve`'s `uMax` derivation: `knots.length - p - 1` is
  // the FIRST index of this array's own trailing `degree+1`-multiplicity
  // clamp run (a structural invariant `ensureClampedAtGrowEnd` exists
  // specifically to guarantee — see its own doc comment). A `+2`-offset
  // index mutant (for `degree >= 2`, the only degrees this package
  // supports) still lands WITHIN that same run — bit-identical value.
  // BEST-EFFORT-EXHAUSTED for a genuinely out-of-bounds offset mutant
  // (mutation-testing skill's convention, NOT an equivalence claim): it
  // would hit the `?? domainUMin` fallback, giving `0` (per the proof
  // above) instead of the true (generally nonzero) domain end — a REAL
  // divergence in principle, but constructing a fixture where this
  // specific out-of-bounds READ is reached (rather than Stryker's
  // instrumentation itself failing on the corrupted expression, or an
  // earlier guard rejecting the input) was not achieved within this
  // gate's time budget.
  // Stryker disable next-line ArithmeticOperator: a +2 offset lands within the same trailing full-clamp-multiplicity run (bit-identical value, same pattern as extractNurbsSubcurve's uMax); the further out-of-bounds offset is best-effort-exhausted (see notes above)
  const domainUMax = knots[knots.length - p - 1] ?? domainUMin;
  const breakpoints = nurbsKnotSpanBreakpoints(knots, domainUMin, domainUMax);
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract, `!growEnd` branch only): `breakpoints[1]! - breakpoints[0]!`
  // -> `+`. `nurbsKnotSpanBreakpoints` returns its OWN `domainUMin`
  // argument as `breakpoints[0]` by construction — and `domainUMin` is
  // proven bit-exact `0` above. `x - 0` and `x + 0` are IDENTICAL in
  // IEEE754 for any finite `x` (adding/subtracting an exact zero never
  // rounds) — no test can kill this. (The `growEnd` branch does NOT share
  // this equivalence — `breakpoints[length-2]` is a genuine, generally
  // nonzero interior breakpoint, and IS independently killed by the
  // existing END-side extend tests.)
  const terminalSpanWidth = growEnd
    ? breakpoints[breakpoints.length - 1]! - breakpoints[breakpoints.length - 2]!
    : // Stryker disable next-line ArithmeticOperator: breakpoints[0] is always domainUMin, proven bit-exact 0 above — subtracting vs adding an exact zero is IEEE754-identical (see equivalent-mutant note above)
      breakpoints[1]! - breakpoints[0]!;
  const uStep = Math.max(terminalSpanWidth, GEO_EPS);
  const uFar = growEnd ? uJoin + uStep : uJoin - uStep;

  // p+1 new points: index 0 duplicates the anchor's VALUE (position
  // continuity), index p is `target`, evenly spaced (collinear ⇒ a genuinely
  // straight sub-curve for ANY degree).
  const newPts: SplinePoint[] = [];
  for (let j = 0; j <= p; j++) {
    const t = j / p;
    newPts.push({
      x: anchor.x + (target.x - anchor.x) * t,
      y: anchor.y + (target.y - anchor.y) * t,
      z: anchorZ + (targetZ - anchorZ) * t,
    });
  }

  const farKnots = new Array(p + 1).fill(uFar);
  // p+1 NEW control points are required (a valid degree-p Bezier segment
  // needs p+1 points), including `newPts[0]` which DUPLICATES the anchor's
  // VALUE as a distinct array slot — see the doc comment above: the old
  // control point stays in place (unchanged) and the join is a SEPARATE
  // interior knot run of multiplicity `degree+1`, not a shared array index.
  // `newWeightSegment` is `undefined` iff `weights` is `undefined` (checked
  // together below via `newWeightSegment` alone — testing `weights` again
  // would be a redundant, provably-always-agreeing second condition).
  const newWeightSegment = weights ? new Array(p + 1).fill(1) : undefined;

  if (growEnd) {
    return {
      controlPoints: [...controlPoints, ...newPts],
      knots: [...knots, ...farKnots],
      weights: newWeightSegment ? [...weights!, ...newWeightSegment] : undefined,
    };
  }
  // Start-side: mirror order (newPts[0]=target .. newPts[p]=anchor-duplicate).
  const reversed = [...newPts].reverse(); // [target, ..., anchorDup]
  return {
    controlPoints: [...reversed, ...controlPoints],
    knots: [...farKnots, ...knots],
    weights: newWeightSegment ? [...newWeightSegment, ...weights!] : undefined,
  };
}

/**
 * `buildLinearExtension` identifies its anchor/join purely by ARRAY POSITION
 * (`controlPoints[0]`/`[length-1]`, `knots[0]`/`[length-1]`) — correct ONLY
 * for a fully CLAMPED end (interior/true-end knot multiplicity `degree+1`,
 * where the outermost control point IS the curve's own endpoint value by the
 * standard clamped-B-spline property). A valid but UNCLAMPED open spline (a
 * DXF SPLINE can legitimately declare one) has its VISIBLE end at
 * `knots[degree]`/`knots[length-degree-1]` instead, generally evaluating to
 * a position DIFFERENT from the outermost control point — anchoring there
 * unclamped would join the new tail at the wrong point AND expose a
 * previously out-of-[uMin,uMax]-domain curve span (Codex P1, round 3).
 *
 * Fix: clamp the growing end via knot insertion up to full `degree+1`
 * multiplicity at its TRUE parameter (curve-preserving — inserting a knot
 * never changes `C(u)`, see `nurbsKnotOps.ts`'s own doc comment), THEN
 * TRUNCATE away everything beyond that point. Insertion ALONE is not
 * sufficient (Codex P1, round 4): it refines the LOCAL control polygon
 * around `uEnd` but does not remove the knots/control points that were
 * already beyond the visible domain — `buildLinearExtension`'s last-array-
 * entry anchor would then pick up a control point from THAT still-present
 * invisible continuation, not the curve's true endpoint, and for degree ≥ 3
 * the exposed continuation can be curved and non-tangent before it
 * eventually reaches whatever the raw knot array's own last value was.
 * Truncation reuses the SAME "already fully clamped" slicing pattern
 * `splitNurbsCurve` (`nurbsKnotOps.ts`) uses for its own left/right pieces
 * once a knot run reaches full multiplicity — a no-op structurally
 * equivalent to "split at `uEnd`, keep only the visible side".
 */
function ensureClampedAtGrowEnd(
  controlPoints: SplinePoint[],
  knots: number[],
  weights: number[] | undefined,
  degree: number,
  growEnd: boolean,
): { controlPoints: SplinePoint[]; knots: number[]; weights: number[] | undefined } {
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract, `!growEnd` branch only — the `growEnd` branch IS
  // independently killed by the existing END-side extend tests): same
  // proof as `extractNurbsSubcurve`'s `uMin` — `knots` is always the
  // rescaled (or `ensureClampedAtGrowEnd`-preserved) knot vector, so
  // `knots[degree]` is the bit-exact literal `0`.
  // Stryker disable next-line LogicalOperator: knots[degree] is always the bit-exact literal 0 (same proof as extractNurbsSubcurve's uMin — see equivalent-mutant note above)
  const uEnd = growEnd ? (knots[knots.length - degree - 1] ?? 0) : (knots[degree] ?? 0);
  const s = knotMultiplicity(uEnd, knots);
  const r = degree + 1 - s;
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract): `r > 0` -> `r >= 0` / `true` (unconditional). `insertKnot`
  // (`nurbsKnotOps.ts`) loops `for (i = 0; i < times; i++)` — for ANY
  // `times <= 0` (whether `r === 0`, ALREADY fully clamped, or `r < 0`, MORE
  // than `degree+1` copies already present) that loop body never executes,
  // returning `{controlPoints, knots, weights}` completely UNCHANGED —
  // structurally IDENTICAL to this ternary's own `false`-branch fallback.
  // So for `r <= 0`, calling `insertKnot` anyway (a `>=0`/`true` mutant)
  // gives the SAME result as skipping it; for `r > 0`, BOTH the correct
  // condition and either mutant take the SAME `insertKnot` branch. No `r`
  // value makes this observable.
  // Stryker disable next-line EqualityOperator,ConditionalExpression: insertKnot with any times<=0 is a structural no-op (loop never executes), identical to the false-branch fallback for every r value (see equivalent-mutant note above)
  const refined = r > 0 ? insertKnot(controlPoints, knots, weights, degree, uEnd, r) : { controlPoints, knots, weights };
  // `uEnd` now has multiplicity exactly `degree+1` in `refined` (either just
  // achieved via insertion, or already the case — `r <= 0` — for an
  // already-clamped end).
  //
  // Locate the run's LAST index via a DIRECT scan, NOT `findKnotSpan` —
  // `findKnotSpan` clamps `u >= uMax-KNOT_EPS` to the CONTROL-POINT-space
  // index `n` (correct for EVALUATION, always wanting the last valid span),
  // but `uEnd` here is BY CONSTRUCTION always exactly at (or beyond, before
  // insertion) `refined`'s own true end — so that clamp branch always fires
  // and returns a value UNRELATED to the run's actual position in the
  // (possibly still-longer, pre-truncation) knot array. Using it here
  // (an EARLIER, now-fixed version of this function did) silently truncated
  // an ALREADY-clamped curve down to a single control point.
  // Tolerance MUST match `knotMultiplicity`'s own `KNOT_EPS` exactly (Codex
  // P2, round 11) — a separately-hardcoded literal here could drift out of
  // sync with the SAME "same knot" decision `s`/`r` above already made.
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract): `-1` -> `+1` (UnaryOperator, the "not found" sentinel).
  // `uEnd` is ALWAYS itself one of `knots`'/`refined.knots`' own array
  // values (`knots[knots.length-degree-1] ?? 0` / `knots[degree] ?? 0`,
  // both proven bit-exact reads elsewhere in this file), so the scan
  // below is guaranteed to find at LEAST that originating index (distance
  // exactly `0`) — the sentinel is NEVER actually read; the loop always
  // overwrites it before `runEndIdx` is used.
  // Stryker disable next-line UnaryOperator: uEnd is always itself a value already present in the array being scanned, so the scan always finds a match — this sentinel is never actually read (see equivalent-mutant note above)
  let runEndIdx = -1;
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract): `i < refined.knots.length` -> `i <= refined.knots.length`.
  // The one phantom extra iteration reads `refined.knots[refined.knots
  // .length]`, which is `undefined` for a plain array (JS semantics) —
  // `Math.abs(undefined - uEnd)` is `NaN`, and `NaN <= NURBS_KNOT_EPS` is
  // `false` (IEEE754: every comparison against `NaN` is `false`) — the
  // phantom iteration never assigns `runEndIdx`, a complete state no-op.
  // Stryker disable next-line EqualityOperator: the phantom out-of-bounds iteration reads undefined, giving NaN, which never satisfies the <= comparison (see equivalent-mutant note above)
  for (let i = 0; i < refined.knots.length; i++) {
    // BEST-EFFORT-EXHAUSTED BOUNDARY (mutation-testing skill's convention
    // — NOT claimed as a hard equivalence proof, though closely
    // reasoned): `<=` -> `<`. Every GENUINE match in this scan is a
    // BIT-EXACT copy of `uEnd` (either the original source knot itself,
    // or one of `insertKnot`'s literal copies — knot insertion inserts
    // the EXACT numeric value passed in, never a recomputed
    // approximation), giving `Math.abs(...) === 0` exactly — for which
    // `<=` and `<` against any positive `NURBS_KNOT_EPS` agree (`true`
    // either way). A genuine NON-match is a DISTINCT knot value, generally
    // far outside `NURBS_KNOT_EPS`'s tolerance band (this file's knot
    // vectors are never constructed with adversarially-close-but-distinct
    // interior knots). No test within this gate's time budget constructed
    // a knot exactly `NURBS_KNOT_EPS` away from `uEnd` — the same class of
    // gap as this package's other exact-epsilon-boundary notes.
    // Stryker disable next-line EqualityOperator: every genuine match is a bit-exact copy of uEnd (distance exactly 0), where <= and < agree; no fixture within this gate's budget landed exactly at the NURBS_KNOT_EPS boundary itself (see best-effort-exhausted note above)
    if (Math.abs(refined.knots[i]! - uEnd) <= NURBS_KNOT_EPS) runEndIdx = i;
  }
  const sharedIdx = runEndIdx - degree;
  const j = sharedIdx + 1;
  if (growEnd) {
    // Keep [uMin, uEnd] — discard whatever was beyond it.
    return {
      controlPoints: refined.controlPoints.slice(0, sharedIdx),
      knots: refined.knots.slice(0, j + degree),
      weights: refined.weights?.slice(0, sharedIdx),
    };
  }
  // Keep [uEnd, uMax] — discard whatever was before it. Rebuilds a FRESH
  // `degree+1`-copy clamp at the start (mirrors `splitNurbsCurve`'s own
  // right-side formula, which is correct regardless of the pre-insertion
  // regime).
  return {
    controlPoints: refined.controlPoints.slice(sharedIdx),
    knots: [...new Array(degree + 1).fill(uEnd), ...refined.knots.slice(j + degree)],
    weights: refined.weights?.slice(sharedIdx),
  };
}

/**
 * Extend a SPLINE target: grow the end nearest the pick with a straight,
 * tangent tail reaching the nearest boundary crossing beyond it. A CLOSED
 * spline has no dangling end (`null`).
 */
export function extendSplineTarget(
  spline: SplineTargetInput,
  boundaryPoints: IntPoint[],
  pickPoint: IntPoint,
): SplineTargetResult | null {
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract, the `boundaryPoints.length === 0` sub-condition ONLY — the
  // `closed`/`periodic` operands ARE independently killed by the tests
  // below, which use a WORKING boundary/pick to distinguish them):
  // disabling JUST this clause (a `false` stub on the third `||` operand)
  // still returns `null` when `boundaryPoints` is genuinely empty — the
  // `for (const bp of boundaryPoints)` loop below simply never executes
  // (zero iterations), `best` stays `null`, and `if (!best) return null;`
  // (further down) already returns `null` via that redundant, independent
  // path. No `spline`/`boundaryPoints` combination makes disabling only
  // this sub-clause observably diverge from keeping it.
  // Stryker disable next-line ConditionalExpression: an empty boundaryPoints array is caught redundantly by the later `if (!best) return null` fallback regardless of this clause (see equivalent-mutant note above)
  if (spline.closed || spline.periodic || boundaryPoints.length === 0) return null;
  const resolved = resolveSplineNurbs(spline);
  if (!resolved) return null;
  const { controlPoints, weights, degree } = resolved;
  // EQUIVALENT MUTANT (mutation-testing skill's equivalent-mutant
  // contract): `controlPoints.length < 2` -> `false` / `<= 2`.
  // `resolveSplineNurbs` already rejects `cps.length === 0`, so the ONLY
  // way to reach this guard with it still `true` is EXACTLY 1 control
  // point. A B-spline's basis functions partition unity (`sum(N_i,p(u)) =
  // 1` for every `u` in the domain, Piegl & Tiller §2) — with exactly ONE
  // control point there is exactly ONE nonzero basis function, which must
  // therefore be identically `1` everywhere, so `evaluateNurbs` returns
  // that SAME single point for EVERY `u`, for ANY degree or knot vector.
  // Disabling this guard therefore ALWAYS reaches the later `dirLen <
  // GEO_EPS` check (`anchor` and `nearPt`, both evaluated on this
  // constant curve, coincide to floating-point roundoff) — a genuinely
  // DIFFERENT guard already independently returning `null`, via a
  // provably redundant path. A `<= 2` mutant is EVEN MORE restrictive
  // (rejects a perfectly valid 2-point linear curve too) and IS killed —
  // see the "exactly 2 control points" test.
  // Stryker disable next-line ConditionalExpression: the only reachable case (exactly 1 control point) always hits the later dirLen<GEO_EPS guard instead — a single control point's curve is provably constant (B-spline partition-of-unity), see equivalent-mutant note above
  if (controlPoints.length < 2) return null;

  // Always normalise to `RESCALE_TARGET_WIDTH` — see `rescaleKnotDomain`'s
  // doc comment (`trimExtendGeometry.ts` — the SHARED implementation, used
  // by `splineEndStretch` too, Codex P2 round 10 #2: `ensureClampedAtGrowEnd`
  // 's `knotMultiplicity`/direct-scan tolerances are the SAME fixed ~1e-9
  // scale as `trimSplineTarget`'s, and suffer the identical tiny-knot-
  // domain failure mode — e.g. `knotMultiplicity(0)` on knots
  // `[0,0,0,0,1e-9,2e-9,...]` wrongly counts the DISTINCT `1e-9` interior
  // knot as part of the start clamp, corrupting the curve). Geometry
  // (`evaluateNurbs`'s x/y/z output) is entirely invariant to this
  // reparameterisation — only `knots`' own numeric scale changes.
  const rescale = rescaleKnotDomain(resolved.knots, degree);
  if (!rescale) return null;
  const { knots, uMin, uMax, toRaw } = rescale;

  const startPt = evaluateNurbs(controlPoints, knots, weights, degree, uMin);
  const endPt = evaluateNurbs(controlPoints, knots, weights, degree, uMax);
  const distStart = Math.hypot(pickPoint.x - startPt.x, pickPoint.y - startPt.y);
  const distEnd = Math.hypot(pickPoint.x - endPt.x, pickPoint.y - endPt.y);
  const growEnd = distEnd <= distStart;
  const anchor = growEnd ? endPt : startPt;

  // Numeric end-tangent — used ONLY to pick the correct boundary candidate
  // (the one lying along the forward ray); the actual new control points are
  // placed by direct interpolation toward the CHOSEN target (see
  // `buildLinearExtension`), so no derivative magnitude is needed.
  //
  // Step size MUST match `trimExtendGeometry.ts`'s `splineEndStretch` EXACTLY
  // — that function is what actually GENERATES the infinite carrier ray
  // `boundaryPoints` were intersected against, via the SAME
  // `splineEndTangentStep` call (Codex P1 round 3: an independently-chosen
  // step gives a numerically DIFFERENT tangent direction on a curved
  // endpoint, causing a genuine carrier hit to fail the perpendicular check
  // below; Codex P2 round 6: a step wider than the curve's own terminal
  // knot span gives a long secant instead of the true endpoint tangent for
  // a nonuniform knot vector — `splineEndTangentStep` bounds against both).
  const h = splineEndTangentStep(knots, degree, !growEnd);
  // `nearU` sits slightly INSIDE the curve from the growing end in both
  // cases, so `anchor − nearPt` always points OUTWARD (the forward growth
  // direction) regardless of which end is growing.
  const nearU = growEnd ? Math.max(uMin, uMax - h) : Math.min(uMax, uMin + h);
  const nearPt = evaluateNurbs(controlPoints, knots, weights, degree, nearU);
  const dirX = anchor.x - nearPt.x;
  const dirY = anchor.y - nearPt.y;
  const dirLen = Math.hypot(dirX, dirY);
  // BEST-EFFORT-EXHAUSTED BOUNDARY (mutation-testing skill's convention —
  // NOT claimed as an equivalence proof, both the `false`-stub
  // ConditionalExpression AND the `<=` EqualityOperator mutants): the
  // EXACT `dirLen === 0` case IS provably a no-op — `ux = dirX/dirLen =
  // 0/0 = NaN`, and NaN propagates through EVERY downstream comparison
  // (`t < GEO_EPS`, `hypot(...) > tolerance`, `t < bestT`) as `false`
  // (IEEE754: any comparison against NaN is `false`), so `best` is NEVER
  // assigned for ANY candidate — the loop always falls through to the
  // LATER `if (!best) return null;`, an independent, redundant path. BUT
  // `dirLen` is never EXACTLY `0` through the normal flow (`h` has its own
  // `Math.max(..., 1e-12)` floor in `splineEndTangentStep`) — the only way
  // to reach a degenerate `dirLen` at all is a curve whose terminal knot
  // span's control points all coincide, which drives `dirLen` to
  // FLOATING-POINT-ROUNDOFF scale (empirically ~1e-15, from the weight
  // sum not summing to EXACTLY `1.0`) rather than the bit-exact `0` the
  // NaN argument needs. Verified directly (via such a fixture) that this
  // near-zero (not exactly-zero) case ALSO empirically falls through to
  // the SAME later `if (!best)` null — consistent with, but not a hard
  // proof of, the same conclusion — no fixture within this gate's time
  // budget produced an OBSERVABLY different (non-null or corrupted)
  // result with this guard disabled.
  // Stryker disable next-line ConditionalExpression,EqualityOperator: exact dirLen===0 is a provable NaN-propagation no-op (every downstream comparison false, falls through to the later if(!best) null); the only reachable near-zero case empirically resolves the same way (see best-effort-exhausted note above)
  if (dirLen < GEO_EPS) return null;
  const ux = dirX / dirLen;
  const uy = dirY / dirLen;
  // dz per unit XY distance along the SAME local tangent (Codex P2, round
  // 5) — a non-planar/tilted-planar spline's endpoint can have a genuine
  // dz/du; `buildLinearExtension` used to force the whole tail flat to the
  // anchor's own Z, introducing a 3-D kink and potentially leaving the
  // spline's declared plane. `dirLen` is already confirmed `>= GEO_EPS`
  // above, so this division is always well-defined.
  const anchorZ = anchor.z ?? 0;
  const zPerXY = ((anchor.z ?? 0) - (nearPt.z ?? 0)) / dirLen;

  let best: SplinePoint | null = null;
  let bestT = Infinity;
  for (const bp of boundaryPoints) {
    const t = (bp.x - anchor.x) * ux + (bp.y - anchor.y) * uy;
    if (t < GEO_EPS) continue;
    // Must lie ON the forward ray — scaled to the CURVE's own extent, NEVER
    // to `t` (Codex P1, round 6). `boundaryPoints` is the union of crossings
    // against the WHOLE combined carrier `infiniteTargetPolyline`'s "Spline"
    // case builds (start stretch + real curve + end stretch) — a crossing
    // that belongs ONLY to the OPPOSITE end's ray can still lie within a
    // `t`-proportional tolerance when the two rays are nearly parallel but
    // offset (e.g. 1 unit apart at t=200 passes a naive 1%-of-t=2.0 check,
    // unboundedly worse the farther the boundary edge is). Since
    // `splineEndStretch` (the carrier generator) and this filter now use the
    // IDENTICAL `splineEndTangentStep`-derived tangent, a GENUINE same-ray
    // crossing is exact up to ordinary floating-point roundoff — the
    // tolerance only needs to absorb THAT, scaled to the curve's own chord
    // (a stable, candidate-independent reference length, matching
    // `trimExtendPolylineTarget.ts`'s `filterToLineCarrier` convention of
    // scaling to the carrier's own natural size, never the candidate's
    // distance along it) — CLAMPED into `[CARRIER_TOLERANCE_FLOOR,
    // CARRIER_TOLERANCE_CEILING]` (Codex P2, round 7): an open curve whose
    // two endpoints coincide or are very close collapses the RAW chord to
    // `GEO_EPS`, giving an unclamped tolerance of `~1e-15` — tighter than
    // ordinary floating-point roundoff at typical world coordinates, which
    // would reject even a genuine same-ray hit.
    const curveChord = Math.hypot(endPt.x - startPt.x, endPt.y - startPt.y);
    const perpX = anchor.x + ux * t - bp.x;
    const perpY = anchor.y + uy * t - bp.y;
    if (Math.hypot(perpX, perpY) > clampCarrierTolerance(curveChord * 1e-6)) continue;
    if (t < bestT) {
      bestT = t;
      // Z follows the SAME local tangent ratio, projected out to this
      // candidate's own XY distance from the anchor (`t`, already the
      // exact forward-ray distance computed above) — a genuinely tangent
      // 3-D extension, not a flattened one.
      best = { x: bp.x, y: bp.y, z: anchorZ + zPerXY * t };
    }
  }
  if (!best) return null;

  const clamped = ensureClampedAtGrowEnd(controlPoints, knots, weights, degree, growEnd);
  const extended = buildLinearExtension(
    clamped.controlPoints,
    clamped.knots,
    clamped.weights,
    degree,
    growEnd,
    best,
  );
  return {
    degree,
    controlPoints: extended.controlPoints,
    knots: extended.knots.map(toRaw), // back to the SOURCE domain scale
    weights: extended.weights,
    closed: false,
    rational: extended.weights !== undefined,
  };
}
